/**
 * Device-type → configuration list registry (#999 Story 3.9 / #1401).
 * Keeps LocationViewStepList from duplicating ×6 prop branches.
 */

import React from 'react';

import { DEVICES_TABLE_MODE } from './devicesTableMode';
import { CoolingSystemConfigurationList } from '../coolingsystems/CoolingSystemConfigurationList';
import { LargeUpsConfigurationList } from '../largeups/LargeUpsConfigurationList';
import { LiquidCoolingCduConfigurationList } from '../liquidcoolingcdus/LiquidCoolingCduConfigurationList';
import { PowerDistributionConfigurationList } from '../powerdistribution/PowerDistributionConfigurationList';
import { RackConfigurationList } from '../racks/RackConfigurationList';
import { SmallUpsConfigurationList } from '../smallups/SmallUpsConfigurationList';

import type { CoolingSystemConfiguration } from '../../types/coolingSystem';
import type { LargeUpsConfiguration } from '../../types/largeUps';
import type { LiquidCoolingCduConfiguration } from '../../types/liquidCoolingCdu';
import type { PowerDistributionConfiguration } from '../../types/powerDistribution';
import type { RackConfiguration } from '../../types/rack';
import type { SmallUpsConfiguration } from '../../types/smallUps';
import type {
  DeviceSourceUnit,
  DeviceStepId,
} from '../../utils/deviceRowMapper';

export type DevicesListSharedProps = typeof DEVICES_TABLE_MODE & {
  allowReorder: boolean;
  onReorderSection?: (
    section: 'existing' | 'new',
    fromIndex: number,
    toIndex: number,
  ) => void;
  disabled: boolean;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
  testID: string;
};

type DevicesStepListRenderer = (params: {
  units: readonly unknown[];
  onPress: (unit: DeviceSourceUnit) => void;
  onAdd: () => void;
  shared: DevicesListSharedProps;
}) => React.ReactElement;

const registry: Record<DeviceStepId, DevicesStepListRenderer> = {
  'rack-power': ({ units, onPress, onAdd, shared }) => (
    <RackConfigurationList
      racks={units as RackConfiguration[]}
      onRackPress={onPress as (rack: RackConfiguration) => void}
      onAddRack={onAdd}
      {...shared}
    />
  ),
  'large-ups': ({ units, onPress, onAdd, shared }) => (
    <LargeUpsConfigurationList
      units={units as LargeUpsConfiguration[]}
      onLargeUpsPress={onPress as (unit: LargeUpsConfiguration) => void}
      onAddLargeUps={onAdd}
      {...shared}
    />
  ),
  'small-ups': ({ units, onPress, onAdd, shared }) => (
    <SmallUpsConfigurationList
      units={units as SmallUpsConfiguration[]}
      onSmallUpsPress={onPress as (unit: SmallUpsConfiguration) => void}
      onAddSmallUps={onAdd}
      {...shared}
    />
  ),
  'power-distribution': ({ units, onPress, onAdd, shared }) => (
    <PowerDistributionConfigurationList
      units={units as PowerDistributionConfiguration[]}
      onPowerDistributionPress={
        onPress as (unit: PowerDistributionConfiguration) => void
      }
      onAddPowerDistribution={onAdd}
      {...shared}
    />
  ),
  'cooling-readiness': ({ units, onPress, onAdd, shared }) => (
    <CoolingSystemConfigurationList
      units={units as CoolingSystemConfiguration[]}
      onCoolingSystemPress={
        onPress as (unit: CoolingSystemConfiguration) => void
      }
      onAddCoolingSystem={onAdd}
      {...shared}
    />
  ),
  'liquid-cooling': ({ units, onPress, onAdd, shared }) => (
    <LiquidCoolingCduConfigurationList
      units={units as LiquidCoolingCduConfiguration[]}
      onLiquidCoolingCduPress={
        onPress as (unit: LiquidCoolingCduConfiguration) => void
      }
      onAddLiquidCoolingCdu={onAdd}
      {...shared}
    />
  ),
};

export function renderDevicesStepList(
  stepId: string,
  params: {
    units: readonly unknown[];
    onPress: (unit: DeviceSourceUnit) => void;
    onAdd: () => void;
    shared: DevicesListSharedProps;
  },
): React.ReactElement | null {
  const render = registry[stepId as DeviceStepId];
  if (!render) {
    return null;
  }
  return render(params);
}

export const DEVICES_STEP_IDS = Object.keys(registry) as DeviceStepId[];
